import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { computeInsights } from "../../lib/internalInsights";
import { stageLabel } from "../../lib/stageLabels";
import { Loader2, TrendingUp, AlertTriangle, Flame, Activity, ListChecks, ShieldCheck, ArrowRight, Brain } from "lucide-react";

const statusColor = { ok: "text-emerald-400 border-emerald-500/40", pending: "text-amber-400 border-amber-500/40", todo: "text-rose-400 border-rose-500/40" };

export default function Operations() {
  const { user } = useAuth();
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [pr, db] = await Promise.all([
          api.get("/prospects").then((r) => r.data).catch(() => []),
          api.get("/internal/dashboard").then((r) => r.data).catch(() => null),
        ]);
        setInsights(computeInsights({ prospects: pr || [], dashboard: db }));
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="flex items-center gap-2 text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading operations…</div>;
  if (!insights) return null;

  return (
    <div className="space-y-6" data-testid="operations-page">
      <header>
        <div className="tick-label text-sky-400 mb-1">/ Internal Operations · Command Center</div>
        <h1 className="font-display font-black text-3xl text-slate-100 leading-tight">Hello{user?.name ? `, ${user.name.split(" ")[0]}` : ""} &mdash; here&apos;s the business in one glance.</h1>
        <p className="text-slate-400 text-sm mt-1">Read-only insights from your own internal data. No public leakage. No record mutation.</p>
      </header>

      {/* Top: KPIs */}
      <div className="grid sm:grid-cols-4 gap-px bg-[var(--border-soft)]">
        <Kpi label="Total Prospects"  v={insights.lead_quality?.prospects}        icon={TrendingUp} />
        <Kpi label="Qualified Rate"   v={insights.lead_quality ? `${insights.lead_quality.qualified_rate}%` : "—"} icon={Activity} />
        <Kpi label="Paid Customers"   v={insights.customer_activity?.paid_customers} accent="emerald" icon={ShieldCheck} />
        <Kpi label="Active Pipeline"  v={insights.sales_priorities.length} icon={Flame} />
      </div>

      {/* Sales Priorities + Hot Prospects */}
      <div className="grid lg:grid-cols-2 gap-px bg-[var(--border-soft)]">
        <Panel title="Sales Priorities" subtitle="Active stages, ranked by Intelligence-Report score" icon={Flame}>
          {insights.sales_priorities.length === 0 ? (
            <Empty msg="No prospects in active stages yet." />
          ) : (
            <ul className="space-y-2" data-testid="ops-sales-priorities">
              {insights.sales_priorities.map((p) => (
                <li key={p.id} className="flex items-center justify-between gap-3 text-sm">
                  <Link to={`/internal/prospects/${p.id}`} className="text-slate-100 hover:text-sky-300 truncate flex-1">{p.name}</Link>
                  <div className="flex items-center gap-2 font-mono text-[10px] tracking-widest uppercase shrink-0">
                    {p.score != null && <span className="text-sky-400">{p.score}</span>}
                    {p.priority && <span className="text-amber-300 border border-amber-500/40 px-1.5 py-0.5">{p.priority}</span>}
                    <span className="text-slate-500">{stageLabel(p.stage)}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel title="Hot Prospects" subtitle="Grade A or Immediate priority" icon={Flame}>
          {insights.hot_prospects.length === 0 ? (
            <Empty msg="No Grade-A / Immediate-priority prospects yet — run more Intelligence Reports to surface them." />
          ) : (
            <ul className="space-y-2" data-testid="ops-hot-prospects">
              {insights.hot_prospects.map((p) => (
                <li key={p.id} className="flex items-center justify-between gap-3 text-sm">
                  <Link to={`/internal/prospects/${p.id}`} className="text-slate-100 hover:text-sky-300 truncate flex-1">{p.name}</Link>
                  <div className="flex items-center gap-2 font-mono text-[10px] tracking-widest uppercase shrink-0">
                    {p.grade && <span className="text-emerald-300 border border-emerald-500/40 px-1.5 py-0.5">Grade {p.grade}</span>}
                    {p.priority && <span className="text-rose-300 border border-rose-500/40 px-1.5 py-0.5">{p.priority}</span>}
                    {p.score != null && <span className="text-sky-400">{p.score}</span>}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>

      {/* Stalled + Customer Activity */}
      <div className="grid lg:grid-cols-2 gap-px bg-[var(--border-soft)]">
        <Panel title="Stalled Prospects" subtitle=">14 days without activity, not closed" icon={AlertTriangle} accent="rose">
          {insights.stalled.length === 0 ? (
            <Empty msg="Everything moving — no stalled prospects." />
          ) : (
            <ul className="space-y-2" data-testid="ops-stalled">
              {insights.stalled.map((p) => (
                <li key={p.id} className="flex items-center justify-between gap-3 text-sm">
                  <Link to={`/internal/prospects/${p.id}`} className="text-slate-100 hover:text-sky-300 truncate flex-1">{p.name}</Link>
                  <div className="flex items-center gap-2 font-mono text-[10px] tracking-widest uppercase shrink-0">
                    <span className="text-rose-300">{p.days_idle}d idle</span>
                    <span className="text-slate-500">{stageLabel(p.stage)}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel title="Customer Activity" subtitle="Aggregated from your own dashboard rollups" icon={Activity}>
          {insights.customer_activity ? (
            <div className="space-y-2 text-sm">
              <RowKV k="Paid Customers"        v={insights.customer_activity.paid_customers} />
              <RowKV k="Repeat Customers"      v={insights.customer_activity.repeat_customers} />
              <RowKV k="Proposals Sent"        v={insights.customer_activity.assessments_delivered} />
            </div>
          ) : <Empty msg="No customer rollup data available." />}
        </Panel>
      </div>

      {/* Discovery signal + system health */}
      <div className="grid lg:grid-cols-2 gap-px bg-[var(--border-soft)]">
        <Panel title="AI Discovery Insights" subtitle="Public funnel signals" icon={Brain}>
          <div className="text-sm text-slate-300 leading-relaxed">
            {insights.discovery_signal?.note}
          </div>
          <div className="mt-3 inline-flex items-center gap-1.5 font-mono text-[10px] tracking-widest uppercase text-amber-300 border border-amber-500/40 px-2 py-0.5">
            Placeholder · Phase 5
          </div>
        </Panel>

        <Panel title="System Health" subtitle="Engine status + Phase 5 hardening backlog" icon={ShieldCheck}>
          <ul className="space-y-1.5">
            {insights.system_health.map((s, i) => (
              <li key={i} className="flex items-start justify-between gap-3 text-sm">
                <div>
                  <div className="text-slate-100">{s.label}</div>
                  <div className="text-xs text-slate-500">{s.detail}</div>
                </div>
                <span className={`font-mono text-[9px] tracking-widest uppercase px-2 py-0.5 border shrink-0 ${statusColor[s.status]}`}>{s.status}</span>
              </li>
            ))}
          </ul>
        </Panel>
      </div>

      {/* Suggested Actions */}
      <Panel title="Suggested Actions" subtitle="Generated from your data + Phase backlog" icon={ListChecks}>
        <ul className="space-y-2">
          {insights.suggested_actions.map((a, i) => (
            <li key={i} className="flex items-start gap-2 text-sm" data-testid={`ops-action-${i}`}>
              <span className={`w-1.5 h-1.5 mt-2 shrink-0 ${a.todo ? "bg-amber-400" : "bg-sky-500"}`} />
              <span className={`${a.todo ? "text-slate-400" : "text-slate-200"} leading-relaxed`}>{a.label}</span>
            </li>
          ))}
        </ul>
        <div className="mt-4 flex flex-wrap gap-2">
          <Link to="/internal/ai-ops" data-testid="ops-cta-ai-ops" className="inline-flex items-center gap-1.5 border border-sky-500/60 hover:bg-sky-500/10 text-sky-300 font-mono text-[10px] tracking-widest uppercase px-3 py-2">
            <Brain className="w-3 h-3" /> Open AI Ops <ArrowRight className="w-3 h-3" />
          </Link>
          <Link to="/internal/crm" className="inline-flex items-center gap-1.5 border border-slate-700 hover:border-slate-500 text-slate-300 font-mono text-[10px] tracking-widest uppercase px-3 py-2">
            CRM Pipeline <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      </Panel>
    </div>
  );
}

function Panel({ title, subtitle, icon: Icon, children, accent }) {
  const ring = accent === "rose" ? "text-rose-400" : "text-sky-400";
  return (
    <section className="bg-[var(--bg-secondary)] p-5">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className={`tick-label ${ring}`}>/ {title}</div>
          <div className="font-mono text-[10px] text-slate-500 mt-0.5">{subtitle}</div>
        </div>
        {Icon && <Icon className={`w-4 h-4 ${ring}`} strokeWidth={2} />}
      </div>
      {children}
    </section>
  );
}

function Kpi({ label, v, accent, icon: Icon }) {
  const color = accent === "emerald" ? "text-emerald-400" : "text-sky-400";
  return (
    <div className="bg-[var(--bg-secondary)] p-4 flex items-center gap-3">
      {Icon && <Icon className={`w-5 h-5 ${color}`} strokeWidth={1.8} />}
      <div>
        <div className="tick-label">{label}</div>
        <div className={`metric-number text-2xl ${color}`}>{v ?? "—"}</div>
      </div>
    </div>
  );
}

function RowKV({ k, v }) {
  return (
    <div className="flex items-baseline justify-between font-mono text-[11px]">
      <span className="text-slate-400 tracking-widest uppercase text-[10px]">{k}</span>
      <span className="text-sky-400 metric-number">{v ?? 0}</span>
    </div>
  );
}

function Empty({ msg }) {
  return <div className="text-sm text-slate-500 italic">{msg}</div>;
}
